import {Component,createEffect,createSignal,For,onMount, onCleanup, Show, JSX } from 'solid-js';
import { render } from "solid-js/web";
import {CMS} from './cms';
import {Resources} from './Data';
import {toQueryString,getPageRange,getPageBlockStart,getOrSetAsync, cleanImagePath, fetchGet} from './API';
import {Map} from './Map';
import { ArtworksGrid } from './ArtworksGrid';
import { AlphabeticalList, makeAccessor, PageList, SelectList, TextInput } from './Controls';
import { ArtworksBlocks } from './ArtworksBlocks';
import * as _ from 'underscore';
import moment from 'moment';
import { LoadingSkeleton } from './components/loading-skeleton/index';
import { createStore } from 'solid-js/store';

type ListItem = { Text: string; Value: string };
type Lists = {
    types: ListItem[],
    countries: ListItem[],
    yesNos: ListItem[],
    imageSizes: ListItem[],
    orderBy: ListItem[]
}
type OrderBy = string | "ByTitle" | "ByDate" | "ByMedium" | "ByRandom" | "ByArtist" | "ByDateAdded" | "ByWidth" | "ByHeight";
type QueryState = "NotStarted" | "Loading" | "Success" | "Error";
type State = {
    query: "no-results"|"results"|"waiting"
}
type Layout = "Grid" | "Block" | "Map";
type ArtworkLocation = {
    id: number;
    name: string;
    country: string;
    countryCode: string;
    details: string;
    url: string;
};
type Artwork = {
    id: number;
    showHiresIcon: boolean;
    alternateTitle: string;
    translateTitle: string;
    medium: string;
    measurements: string;
    credit: string;
    imageHeight: number;
    imageWidth: number;
    content: string;
    artworkLocation?: ArtworkLocation;
    imageCount: number;
    quality: number;
    isForSale: boolean;
    isSold: boolean;
    startYear?: number;
    endYear?: number;
    depictedAddressId?: number;
    dateRange: string;
    dateAdded: string;
    title: string;
    artworkType: string;
    url: string;
    primaryImageUrl: string;
    backgroundColor: string;
};

type InfoWindowArgs = {
    name: string;
    artworks: any[];
};
function InfoWindow (p: InfoWindowArgs) {
  return (
    <>
        <div style="text-align: left; color: #333; max-width: 750px;">
            <h4>{p.name}</h4>
            <For each={p.artworks}>{(x: any) =>
                <div style="display: inline-block; padding: 5px; background-color: #333; text-align: center;">
                    <a href={ "/Artwork/Index/" + x.ArtworkId } target="_blank">
                        <img style="width: 100px; height: 100px;" src={ cleanImagePath(x.PrimaryImageUrl) + "?mode=crop&format=jpg&w=100&h=100" } />
                    </a>
                </div>
            }</For>
        </div>
    </>
  );      
};

export const PreloadArtworksTab : Component<any> = (props) => {
    const [queryState, setQueryState] = createSignal<QueryState>("NotStarted");
    const [id, setId] = createSignal<number>(props.artistId);
    const [data, setData] = createSignal<{ markers: any[]; heatmap: any[] }|null>(null);
    const [state, setState] = createStore<State>({ query: "waiting" });
    const [layout, setLayout] = createSignal<Layout>("Grid");
    const [allArtworks, setAllArtworks] = createSignal<Artwork[]>([]);
    const [artworks, setArtworks] = createSignal<Artwork[]>([]);
    const [title, setTitle] = createSignal<string>("");
    const [type, setType] = createSignal<string>("");
    const [hasDepictedAddress, setHasDepictedAddress] = createSignal<string>("");
    const [medium, setMedium] = createSignal<string>("");
    const [country, setCountry] = createSignal<string>("");
    const [imageSize, setImageSize] = createSignal<string>("");
    const [startsWith, setStartsWith] = createSignal<string>("");
    const [startYear, setStartYear] = createSignal<string>("");
    const [endYear, setEndYear] = createSignal<string>("");
    const [order, setOrder] = createSignal<OrderBy>("ByDefault");
    const [direction, setDirection] = createSignal<OrderBy>("Ascending");
    const [letters, setLetters] = createSignal<string[]>([]);
    const [mapMode, setMapMode] = createSignal<string>("markers");
    const [mapWidth, setMapWidth] = createSignal<string>("100%");
    const [mapHeight, setMapHeight] = createSignal<string>("600px");
    const [titleChars, setTitleChars] = createSignal<string[]>([]);
    const [list, setList] = createStore<Lists>({
        types: (Resources.artworkTypes || []) as any,
        countries: [],
        yesNos: Resources.yesNos || [],
        imageSizes: [
            {Text:"", Value: ""},
            {Text:"Regular (up to 2 MPs)", Value: "M"},
            {Text:"Large (between 2 - 8 MPs)", Value: "L"},
            {Text:"Extra Large (between 8 - 16 MPs)", Value: "XL"},
            {Text:"Extremely Large (more than 16 MPs)", Value: "XXL"}
        ],
        orderBy: [
            { Text: "By default", Value: "ByDefault" },
            { Text: "By title", Value: "ByTitle" },
            { Text: "By date", Value: "ByDate" },
            { Text: "By medium", Value: "ByMedium" },
            { Text: "By height", Value: "ByHeight" },
            { Text: "By width", Value: "ByWidth" },
            { Text: "By date added", Value: "ByDateAdded" },
            { Text: "By random artworks", Value: "ByRandom" }
        ]
    });    
    const setMarkerMode = (e: Event) => {
        e.preventDefault();
        setMapMode("markers");
    }
    const setHeatmapMode = (e: Event) => {
        e.preventDefault();
        setMapMode("heatmap");
    }
    const resetFields = async () => {
        setTitle("");
        setCountry("");
        setType("");
        setMedium("");
        setStartYear("");
        setImageSize("");
        setHasDepictedAddress("");
        setStartsWith("");
        setEndYear(""),
        setOrder("ByDefault");
        setDirection("Ascending");
        await search(true);
    };
    const searchByStartLetter = async (letter: string) => {
        if (layout() === "Map") {
            setLayout("Grid");
        }
        setStartsWith(letter == startsWith() ? "" : letter);
        await search();
    };
    const searchByPage = async (page: number) => {
        if (layout() === "Map") {
            setLayout("Grid");
        }
        await search();
    }
    const submit = async (e: Event) => {
        e.preventDefault();
        await search(true);
    };
    const retrySearch = async () => {
        await search(true);
    };
    const getFilter = () => {
        const parameters = {
            ArtistId: id(),
            Title: title(),
            Medium: medium(),
            Country: country(),
            ArtworkType: type(),
            FromYear: startYear(),
            ToYear: endYear(),
            StartsWith: startsWith(),
            ImageSize: imageSize(),
            HasDepictedAddress: hasDepictedAddress(),
            Order: order(),
            Direction: direction()
        };
        return parameters;
    };
    const getData = async () => {
        const filter = toQueryString(getFilter());
        const json = await getOrSetAsync("artworks/" + filter, async () => {
            const response = await fetch("/Artist/SolidSearchArtworkJson?" + filter);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const preloadArtworks = async () => {
        const res = await fetchGet("/Artist/FetchAllArtworksJson", { id: props.artistId });
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        const results: Artwork[] = json.Data.Items.map((item: any) => {
            const result: Artwork = {
                id: item.Id,
                backgroundColor: item.BackgroundColor,
                title: item.Title,
                alternateTitle: item.AlternateTitle,
                translateTitle: item.TranslatedTitle,
                medium: item.Medium,
                credit: item.Credit,
                content: item.Content,
                quality: item.QualityLevel,
                imageCount: item.ImageCount,
                measurements: item.Measurements,
                showHiresIcon: item.ShowHiresIcon,
                imageHeight: item.ImageHeight || 0,
                imageWidth: item.ImageWidth || 0,
                startYear: parseInt(item.StartYear || "0"),
                endYear: parseInt(item.EndYear || "0"),
                artworkType: item.ArtworkType,
                depictedAddressId: item.DepictedAddressId,
                isForSale: item.IsForSale,
                isSold: item.IsSold,
                url: `/artworks/${item.Artist.Slug}/${item.Slug}/${item.Id}`,
                artworkLocation: !!item.ArtCollection ? {
                    id: item.ArtCollection.Id,
                    url: `/art-collections/${item.ArtCollection.Slug}/${item.ArtCollection.Id}`,
                    name: item.ArtCollection.Name,
                    country: item.ArtCollection.Country,
                    countryCode: item.ArtCollection.CountryCode,
                    details: [item.ArtCollection.City, item.ArtCollection.Country].filter(x => !!x).join(" | "),
                } : undefined,
                primaryImageUrl: item.ImageUrl,
                dateAdded: item.DateAdded,
                dateRange: item.DateRange
            };
            return result;
        });
        const artworkCountries = _.unique(results.filter(x => !!x.artworkLocation?.country).map(x => ({ Text: x.artworkLocation?.country, Value: x.artworkLocation?.countryCode } as ListItem)), (item) => item.Value);
        setList("countries", [{Text: "", Value: ""}].concat(_.sortBy(artworkCountries, "Text")));
        setAllArtworks(results);
    };
    const updateTitleChars = () => {
        const _filter = getFilter();
        const hasFields = _filter.ArtworkType || _filter.Country || _filter.HasDepictedAddress || _filter.FromYear || _filter.ToYear || _filter.Medium || _filter.Title || _filter.ImageSize;
        const recordset = hasFields ? artworks() : allArtworks();
        const characters = getTitleChars(recordset.map(x => x.title));
        setTitleChars(characters);
    }
    const getTitleChars = (titles: string[]) => _.unique(titles.map(x => x[0].toUpperCase()));
    const search = async (reset = true) => {
        applyFilter(reset);
        updateTitleChars();
    };
    const mapIconColour = () => layout() === "Map" ? "red" : "#000";
    const blockIconColour = () => layout() === "Block" ? "red" : "#000";
    const gridIconColour = () => layout() === "Grid" ? "red" : "#000";
    const markerLinkColour = () => mapMode() === "markers" ? "red" : "#efefef";
    const heatmapLinkColour = () => mapMode() === "heatmap" ? "red" : "#efefef";
    const createAlphabet = () => {
        const alpha = Array.from(Array(26)).map((e, i) => i + 65);
        const alphabet = alpha.map((x) => String.fromCharCode(x));
        return alphabet;
    };
    const getMapData = async ()  => {
        const json = await getOrSetAsync("map:markers-and-heatmap/" + props.artistId, async () => {
            const response = await fetch("/Artist/SolidMapOfArtworkLocationsJson?artistId=" + props.artistId);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const getInfoWindowData = async (params: any) => {
        const _params = toQueryString(params);
        const json = await getOrSetAsync("info-window/" + _params, async () =>{
            const response = await fetch("/Artwork/SolidGetArtworksAtGeolocation?" + _params);
            const json = await response.json();
            return json;
        });
        return json;
    };
    const renderToString = (jsx: JSX.Element) => {
        const node = document.createElement("DIV");
        render(() => jsx, node);
        return node.innerHTML;
    };
    const onMarkerClicked = async (marker: google.maps.Marker, map: google.maps.Map) => {
        const latlng = marker.getPosition();
        const params = {
            artistId: props.artistId,
            latitude: latlng?.lat(),
            longitude: latlng?.lng()
        };
        const json = await getInfoWindowData(params);
        const artworks = json.Data;
        const model = {
            name: marker.data.Name,
            artworks
        };
        const jsx = InfoWindow(model);
        const content = renderToString(jsx);
        const infowindow = new google.maps.InfoWindow({ content });
        infowindow.open(map, marker);
    };
    const switchToMapLayout = async () => {
        console.log("switchToMapLayout");
        const json = await getMapData();
        const markers = json.Data.Markers;
        const heatmap = json.Data.Heatmap;
        console.log({ markers, heatmap });
        setData({ markers, heatmap });
        setLayout("Map");
    };
    const applyFilter = (reset = true) => {
        let filter = getFilter();
        let results = allArtworks();
        if (filter.Country) {
            results = results.filter(x => x.artworkLocation?.countryCode == filter.Country);
        }
        if (filter.StartsWith) {
            results = results.filter(x => x.title[0].toLowerCase() == filter.StartsWith.toLowerCase());
        }
        if (filter.HasDepictedAddress) {
            results = results.filter(x => filter.HasDepictedAddress == "Yes" ? (x.depictedAddressId || 0) > 0 : !x.depictedAddressId);
        }
        if (filter.Title) {
            results = results.filter(x => x.title.toLowerCase().indexOf(filter.Title.toLowerCase()) >= 0);
        }
        if (filter.ArtworkType) {
            results = results.filter(x => x.artworkType == filter.ArtworkType);
        }
        if (filter.FromYear) {
            results = results.filter(x => parseInt(filter.FromYear) <= (x.startYear || 0));
        }
        if (filter.ToYear) {
            results = results.filter(x => (x.startYear || 0) <= parseInt(filter.ToYear));
        }
        if (filter.ImageSize) {
            let size = filter.ImageSize;
            results = results.filter(x => {
                let mp = Math.ceil(((x.imageHeight || 1) * (x.imageWidth || 1) || 1) / 1024 / 1024);
                if (size == "M") return mp >= 2 && mp < 4;
                if (size == "L") return mp >= 4 && mp < 8;
                if (size == "XL") return mp >= 8 && mp < 16;
                if (size == "XXL") return mp >= 16;
            });
        }
        if (filter.Medium) {
            results = results.filter(x => x.medium?.toLowerCase().indexOf(filter.Medium.toLowerCase()) >= 0);
        }
        if (filter.Order) {
            let asc = filter.Direction == "Ascending";
            if (filter.Order == "ByDefault") {
                results = sort(results, "quality", "Descending");
            } else if (filter.Order == "ByTitle") {
                results = sort(results, "title", filter.Direction);
            } else if (filter.Order == "ByDate") {
                results = sort(results, "startDate", filter.Direction);
            } else if (filter.Order == "ByMedium") {
                results = sort(results, "medium", filter.Direction);
            } else if (filter.Order == "ByDateAdded") {
                results = results.sort((a,b) => {
                    const ar = moment(a.dateAdded);
                    const br = moment(b.dateAdded);
                    const diff = ar.diff(br);
                    return asc ? diff ? 0 : diff > 0 ? 1 : -1
                               : diff ? 0 : diff > 0 ? -1 : 1
                });
            } else if (filter.Order == "ByWidth") {
                results = results.sort((a,b) => {
                    const ar = (a.imageHeight || 1) / (a.imageWidth || 1);
                    const br = (b.imageHeight || 1) / (b.imageWidth || 1);
                    return asc ? ar == br ? 0 : ar > br ? 1 : -1
                               : ar == br ? 0 : ar > br ? -1 : 1
                });
            } else if (filter.Order == "ByHeight") {
                results = results.sort((a,b) => {
                    const ar = (a.imageWidth || 1) / (a.imageHeight || 1);
                    const br = (b.imageWidth || 1) / (b.imageHeight || 1);
                    return asc ? ar == br ? 0 : ar > br ? 1 : -1
                               : ar == br ? 0 : ar > br ? -1 : 1
                });
            } else {
                results = sort(results, "quality", filter.Direction);
            }
        }
        setArtworks(results);
        updateTitleChars();
        setState({
            query: results.length ? "results" : "no-results"
        });
    }
    const sort = (list: Artwork[], field: string, direction: string) => direction == "Ascending" ? _.sortBy(list, field) : _.chain(list).sortBy(field).reverse().value();
    onMount(async () => {
        setLetters(createAlphabet());
        await preloadArtworks();
        applyFilter(true);
    });
return (
<div style="background-color: #fff;">   
    <form onSubmit={submit}>
        <section class="search" style="border: none; padding-bottom: 20px;">
            <div class="row">
                <div class="col-md-2">
                    <label>
                        <CMS id="label::title">Title</CMS>
                    </label>
                    <TextInput field={[title, setTitle]}></TextInput>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::medium">Medium</CMS>
                    </label>
                    <TextInput field={[medium, setMedium]}></TextInput>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::type">Type</CMS>
                    </label>
                    <SelectList field={[type, setType]} list={() => list.types}></SelectList>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::country">Country</CMS>
                    </label>
                    <SelectList field={[country, setCountry]} list={() => list.countries}></SelectList>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::sort-by">Sort by</CMS>
                    </label>
                    <SelectList field={[order, setOrder]} list={() => list.orderBy}></SelectList>
                </div>
            </div>
            <div class="row" style="margin-top: 10px;">
                <div class="col-md-2">
                    <div class="row">
                        <div class="col-md-6">
                            <label><CMS id="label::from-year">From year</CMS></label>
                            <TextInput field={[startYear, setStartYear]}></TextInput>
                        </div>
                        <div class="col-md-6">
                            <label><CMS id="label::to-year">To year</CMS></label>
                            <TextInput field={[endYear, setEndYear]}></TextInput>
                        </div>
                    </div>
                    <div class="row" style="margin-top: 10px;">
                        <div class="col-md-12">
                            <button style="margin-right: 5px;" type="button" class="btn btn-default" onClick={(e) => { e.preventDefault(); resetFields(); }}><CMS id="button::clear">Clear</CMS></button>
                            <button type="submit" class="btn btn-primary"><CMS id="button::search">Search</CMS></button>
                        </div>
                    </div>
                </div>
                <div class="col-md-1">
                    <div class="check">
                        <label>
                            Image size
                        </label>
                        <SelectList field={[imageSize, setImageSize]} list={() => list.imageSizes}></SelectList>
                    </div>
                </div>
                <div class="col-md-1">
                    <div class="check">
                        <label>
                            Depicts a place
                        </label>
                        <SelectList field={[hasDepictedAddress, setHasDepictedAddress]} list={() => list.yesNos}></SelectList>
                    </div>
                </div>
                <div class="col-md-4">
                    <label><CMS id="label::titles-starting-with">Titles starting with</CMS></label>
                    <div>
                        <AlphabeticalList letter={startsWith} existingLetters={titleChars} onClick={searchByStartLetter}></AlphabeticalList>
                    </div>
                </div>
                <div class="col-md-2">
                    <label>
                        <CMS id="label::view">View</CMS>
                    </label>
                    <div>
                        <div style="float: left; margin-right: 20px;" onClick={(e) => setLayout("Grid")}>
                            <div>
                                <span class="glyphicon glyphicon-th-large" style={{ "font-size": "2em", "color": gridIconColour() }}></span>
                            </div>
                            <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": gridIconColour() }}>
                                <CMS id="label::grid">Grid</CMS>
                            </div>
                        </div>
                        <div style="float: left; margin-right: 20px;" onClick={(e) => setLayout("Block")}>
                            <div>
                                <span class="glyphicon glyphicon-th" style={{ "font-size": "2em", "color": blockIconColour() }}></span>
                            </div>
                            <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": blockIconColour() }}>
                                <CMS id="label::block">Block</CMS>
                            </div>
                        </div>
                        <Show when={props.hasMapLocations}>
                            <div onClick={(e) => switchToMapLayout()}>
                                <div>
                                    <span class="glyphicon glyphicon-map-marker" style={{ "font-size": "2em", "color": mapIconColour() }}></span>
                                </div>
                                <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": mapIconColour() }}>
                                    <CMS id="label::map">Map</CMS>
                                </div>
                            </div>
                        </Show>
                    </div>
                </div>
            </div>
        </section>
    </form>
    <section class="salon-results">
        <div style="background-color: #333; position: relative; text-align: center;">
            <Show when={queryState() == "Success" && artworks().length === 0}>
                <div class="finalist-grid">
                    <div style="text-align: center;">
                        <h2 style="font-size: 3em; color: #fff; padding: 40px; font-family: 'Oswald';">
                            <CMS id="message::search-returned-no-results">This search returned no results.</CMS>
                        </h2>
                    </div>
                </div>
            </Show>
            <Show when={queryState() == "Error"}>
                <div class="finalist-grid">
                    <div style="text-align: center;">
                        <div style="padding: 40px;">
                            <h2 style="font-size: 3em; color: #fff; font-family: 'Oswald';">
                                <CMS id="message::unexpected-search-error">There was an unexpected error. Please try again.</CMS>
                            </h2>
                            <div style="font-size: 2em; margin-top: 30px;">
                                <button type="button" onClick={retrySearch}><span class="fa fa-refresh"></span> <CMS id="button::retry">Retry</CMS></button>
                            </div>
                        </div>
                    </div>
                </div>
            </Show>
            <Show when={layout() === "Grid"}>
                <Show when={state.query == "results"}>
                    <ArtworksGrid isLoggedIn={props.isLoggedIn} artworks={artworks} />
                </Show>
                <Show when={state.query == "no-results"}>
                    <div style="padding: 80px; color: #fff;">
                        <h1>
                            No results found ...
                        </h1>
                        <div style="margin-top: 40px;">
                            <button class="btn btn-default" onClick={(e) => { e.preventDefault(); resetFields() }}>Reset the search</button>
                        </div>
                    </div>
                </Show>
                <Show when={state.query == "waiting"}>
                    <LoadingSkeleton count={4} width={"300px"} height={"200px"} />
                </Show>
            </Show>
            <Show when={layout() === "Block"}>
                <Show when={state.query == "results"}>
                    <ArtworksBlocks isLoggedIn={props.isLoggedIn} artworks={artworks} />
                </Show>
                <Show when={state.query == "waiting"}>
                    <LoadingSkeleton count={4} width={"300px"} height={"200px"} />
                </Show>
                <Show when={state.query == "no-results"}>
                    <div style="padding: 80px; color: #fff;">
                        <h1>
                            No results found ...
                        </h1>
                        <div style="margin-top: 40px;">
                            <button class="btn btn-default" onClick={(e) => { e.preventDefault(); resetFields() }}>Reset the search</button>
                        </div>
                    </div>
                </Show>
            </Show>
            <Show when={layout() === "Map"}>
                <div style="color: #efefef; text-align: left;">
                    <div style="margin: 15px 0; padding: 1px 20px;">
                        <h3 style="font-family: 'Impact'; letter-spacing: 0.15rem; padding-bottom: 10px;">
                            Location of artworks in museums &amp; collections
                        </h3>
                        <div>
                            <div onClick={(e) => setMarkerMode(e) } style="display: inline-block; margin-right: 20px;">
                                <div style="position: relative; left: 15px;">
                                    <span class="glyphicon glyphicon-map-marker" style={{ "font-size": "2em", "color": mapIconColour() }}></span>
                                </div>
                                <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": markerLinkColour() }}>
                                    <CMS id="label::map">markers</CMS>
                                </div>
                            </div>
                            <div onClick={(e) => setHeatmapMode(e) } style="display: inline-block;">
                            <div style="position: relative; left: 15px; top: -5px;">
                                    <img src="/images/icons/heatmap.png" style="height: 30px; width: 30px;" />
                                </div>
                                <div style={{ "font-family": "Oswald", "font-size": "1em", "letter-spacing": "0.1em", "color": heatmapLinkColour() }}>
                                    <CMS id="label::map">heatmap</CMS>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p style="color: #efefef; border-top: 2px dotted #efefef; padding: 10px 20px; margin: 0; font-size: 1.5em;" onClick={() => setLayout("Grid") }>
                        <span class="fa fa-arrow-circle-left" style="margin-right: 10px;"></span> <CMS id="button::back-to-the-artworks">back to the artworks</CMS>
                    </p>
                </div>
                <Map
                    width={mapWidth} height={mapHeight}
                    data={data}
                    onMarkerClicked={onMarkerClicked}
                    mode={mapMode}
                />
            </Show>
        </div>
    </section>
</div>);
}